import React from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";

/**
 * Single reusable sortable-list wrapper for the admin products page.
 *
 * Plug this around any rendered list to make it drag-and-drop reorderable.
 * Will be mirrored in the vendor portal — keep the API tight and obvious.
 *
 * Props:
 *   items         — the array currently being rendered. Each item must
 *                   have a stable id (or pass `getId` to derive one).
 *   onReorder     — (nextArray) => void. Called with the full new array
 *                   in its new order; caller wires it to setState.
 *   getId         — optional accessor. Defaults to `item.id ?? index`.
 *                   For arrays where some items are unsaved (no DB id),
 *                   the index fallback works as long as items don't
 *                   change between renders.
 *   strategy      — "horizontal" | "vertical" | "grid". Default vertical.
 *   renderItem    — (item, index, { dragHandleProps, dragging }) => ReactNode.
 *                   Spread dragHandleProps onto the element that should
 *                   trigger the drag (or use the default <DragHandle /> below).
 *   disabled      — boolean. When true, falls back to plain map() with no DnD.
 *                   Useful while the page is read-only (e.g. non-admin vendor).
 */
export default function SortableList({
  items,
  onReorder,
  getId,
  strategy = "vertical",
  renderItem,
  disabled = false,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require a small drag distance so a normal click on a child button
      // (delete icon, edit icon) isn't interpreted as a drag start.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const resolveId = React.useCallback(
    (item, idx) => {
      if (typeof getId === "function") return String(getId(item, idx));
      if (item && item.id !== undefined && item.id !== null) return String(item.id);
      return `idx-${idx}`;
    },
    [getId],
  );

  const idList = items.map((item, idx) => resolveId(item, idx));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = idList.indexOf(String(active.id));
    const newIndex = idList.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  if (disabled) {
    return (
      <>
        {items.map((item, idx) =>
          renderItem(item, idx, {
            sortableProps: { ref: null, style: {} },
            dragHandleProps: {},
            dragging: false,
          }),
        )}
      </>
    );
  }

  const sortingStrategy =
    strategy === "horizontal"
      ? horizontalListSortingStrategy
      : strategy === "grid"
        ? rectSortingStrategy
        : verticalListSortingStrategy;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={idList} strategy={sortingStrategy}>
        {items.map((item, idx) => (
          <SortableShell
            key={idList[idx]}
            id={idList[idx]}
            index={idx}
            item={item}
            renderItem={renderItem}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableShell({ id, index, item, renderItem }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  // Pass ref + style + drag handle props to the consumer's element so we
  // don't inject a wrapper <div> (which would break <ul>/<li> semantics
  // and any parent flex layout). Caller spreads these onto their existing
  // root element.
  const sortableProps = {
    ref: setNodeRef,
    style: {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      position: "relative",
      zIndex: isDragging ? 10 : "auto",
    },
  };
  const dragHandleProps = { ...attributes, ...listeners };

  return renderItem(item, index, {
    sortableProps,
    dragHandleProps,
    dragging: isDragging,
  });
}

/**
 * Tiny pre-styled drag handle icon. Use inside renderItem like:
 *   <DragHandle {...dragHandleProps} />
 */
export function DragHandle({ style: extraStyle, ...rest }) {
  return (
    <span
      {...rest}
      style={{
        cursor: "grab",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2px 4px",
        color: "#9aa5b1",
        ...(extraStyle || {}),
      }}
      title="Drag to reorder"
    >
      <MdDragIndicator size={18} />
    </span>
  );
}
